import posthog from 'posthog-js';
import { env, isConfigured } from './env';

export function initPostHog() {
  if (!isConfigured.posthog) return;
  posthog.init(env.posthogKey, {
    api_host: env.posthogHost,
    // ── The DOM lockdown. See docs/TELEMETRY.md §The rule ──
    // This app renders annual CTC, bank account numbers and personal contact
    // details. All three of these were documented as "enforced in this file" and
    // were in fact absent, which left autocapture at its posthog-js default of
    // ON — shipping the textContent of everything clicked on /hr and /me.
    //
    // autocapture: reads textContent and element attributes off every click.
    // disable_session_recording: replay records the rendered DOM, and its default
    //   is off-in-code / on-by-dashboard, i.e. the switch lives outside this repo.
    // advanced_disable_flags: closes every remaining remote-config DOM reader in
    //   one line — heatmaps, dead clicks, surveys, web experiments. Web
    //   experiments can inject DOM mutations into a page showing bank details.
    autocapture: false,
    disable_session_recording: true,
    advanced_disable_flags: true,
    // react-router navigates via the History API, so a plain `true` would fire
    // once per hard load and miss every in-app move.
    capture_pageview: 'history_change',
    // PostHog error tracking — auto-capture unhandled errors + promise rejections.
    capture_exceptions: true,
    person_profiles: 'identified_only',
  });
}

/**
 * The ONLY person properties this app may send.
 *
 * A closed type, not a convenience: `role_id` is a slug, and the widening that
 * matters is someone adding `full_name` or `email` here in a hurry. Adding a key
 * has to be a deliberate edit to this type AND to the table in docs/TELEMETRY.md.
 * Role *labels* are user-typed at runtime, so the slug is what ships.
 */
export type PersonProps = { role_id: string };

export function identify(distinctId: string, props: PersonProps) {
  if (!isConfigured.posthog) return;
  // Rebuilt rather than spread, so a caller passing a wider object at runtime
  // (an untyped `profile` row, say) cannot smuggle name or email past the type.
  posthog.identify(distinctId, { role_id: props.role_id });
}

// Report a *handled* error to PostHog error tracking. No-op (→ console in local
// dev) when PostHog isn't configured, so swallowed async failures — optimistic
// write rollbacks, failed RPCs — stay observable instead of vanishing into a toast.
export function captureError(error: unknown, context?: Record<string, unknown>) {
  if (isConfigured.posthog) {
    posthog.captureException(error instanceof Error ? error : new Error(String(error)), context);
  } else if (import.meta.env.DEV) {
    console.error('[captureError]', error, context ?? '');
  }
}

export { posthog };
